import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Copy, CheckCircle, Send, XCircle } from 'lucide-react';

// 🔐 Connexion P3 — statut d'invitation lu côté client (Expirée = en_attente + expire_a passé).
export function statutInvitation(user, maintenant = Date.now()) {
  if (user.invitation_statut === 'en_attente' && user.invitation_expire_a && new Date(user.invitation_expire_a).getTime() < maintenant) return 'expiree';
  return user.invitation_statut || null;
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '');

export function BadgeInvitation({ user }) {
  const s = statutInvitation(user);
  if (!s || s === 'acceptee' && !user.invitation_acceptee_a) return null;
  const map = {
    en_attente: ['bg-blue-100 text-blue-700', 'Invitation en attente'],
    expiree: ['bg-orange-100 text-orange-700', 'Invitation expirée'],
    acceptee: ['bg-emerald-100 text-emerald-700', `Acceptée le ${fmtDate(user.invitation_acceptee_a)}`],
    annulee: ['bg-slate-200 text-slate-600', 'Invitation annulée'],
  };
  const [cls, lib] = map[s] || ['bg-slate-100 text-slate-600', s];
  return <Badge className={`text-xs ${cls}`} data-testid={`badge-invitation-${user.id}`}>{lib}</Badge>;
}

export function ActionsInvitation({ user, onRenvoyer, onAnnuler }) {
  const s = statutInvitation(user);
  if (!['en_attente', 'expiree', 'annulee'].includes(s)) return null;
  return (
    <>
      <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => onRenvoyer(user)} data-testid={`btn-renvoyer-invitation-${user.id}`}>
        <Send className="w-3 h-3 mr-1" />Renvoyer l'invitation
      </Button>
      {s !== 'annulee' && (
        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => onAnnuler(user)} data-testid={`btn-annuler-invitation-${user.id}`}>
          <XCircle className="w-3 h-3 mr-1" />Annuler l'invitation
        </Button>
      )}
    </>
  );
}

// Dialogue affiché UNE fois après création/renvoi : le lien n'est jamais re-lisible ensuite.
export function DialogLienInvitation({ info, onFermer }) {
  const copier = () => { navigator.clipboard.writeText(info.lien_invitation); toast.success('Lien copié !'); };
  return (
    <Dialog open={!!info} onOpenChange={() => onFermer()}>
      <DialogContent className="max-w-lg" data-testid="dialog-lien-invitation">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-green-700"><CheckCircle className="w-5 h-5" />Invitation {info?.renvoi ? 'renvoyée' : 'créée'} !</DialogTitle></DialogHeader>
        {info && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              <strong>{info.prenom}</strong> ({info.email}) doit choisir son mot de passe via le lien ci-dessous, valide jusqu'au <strong>{fmtDate(info.expire_a)}</strong>.
              {info.email_sent ? ' Le courriel d\'invitation a été envoyé.' : ' Le courriel n\'a pas pu être envoyé : partagez ce lien manuellement.'}
            </p>
            <div className="flex items-center gap-2 bg-slate-900 text-green-300 font-mono rounded-lg p-3 text-xs break-all">
              <span className="flex-1" data-testid="lien-invitation">{info.lien_invitation}</span>
              <Button size="sm" variant="ghost" className="text-slate-300 h-7 px-2 shrink-0" onClick={copier} data-testid="copier-lien-invitation"><Copy className="w-3 h-3 mr-1" />Copier le lien</Button>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Ce lien est affiché une seule fois et ne contient aucun mot de passe. Pour un nouveau lien, utilisez « Renvoyer l'invitation ».</p>
          </div>
        )}
        <DialogFooter><Button onClick={onFermer} className="bg-green-600 text-white" data-testid="fermer-lien-invitation">Compris</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
